/**
 * PASEK ZAPEŁNIENIA — jedna implementacja dla listy artykułów i dla rozbicia na automaty.
 *
 * ⚠️ WSPÓLNY, BO KOLORY JUŻ RAZ SKŁAMAŁY. Pasek sięgał po zmienne `--ok` powyżej 67 %
 * i `--danger` poniżej 34 %; motyw definiuje `--success` i `--destructive`, tamtych dwóch nie ma
 * w ogóle. Skutek nie wyglądał na usterkę kolorów: przy 95 % pasek był PUSTY, przy 50 %
 * pomarańczowy i poprawny — czyli „czasem działa”, najgorszy rodzaj objawu. Zgłoszone słowami
 * „paski z % nie są wypełnione, mimo że produkt ma np 95%”.
 *
 * ⚠️ NAZW `--ok` I `--danger` NIE WOLNO TU NAPISAĆ W SKŁADNI `var(…)` NAWET W KOMENTARZU —
 * `motyw-zmienne.spec.ts` skanuje pliki tekstem i słusznie zgłosiłby je jako nieistniejące.
 * Ten test właśnie na tym błędzie powstał, więc nie uczymy go ignorować własnego wzorca.
 *
 * Drugie miejsce rysujące to samo od nowa prędzej czy później rozjedzie się z pierwszym,
 * a próg „ile procent to już czerwony” musi znaczyć na obu ekranach dokładnie to samo.
 */

/**
 * KOLOR STANU — jedno miejsce dla obwódki, tła i wypełnienia.
 *
 * Wyliczany raz, bo wcześniej stan siedział WYŁĄCZNIE w szerokości wypełnienia: przy 0 % nie było
 * czego pokolorować i pasek wyglądał identycznie jak „brak pojemności”. Teraz stan niesie cała
 * ramka, więc pusty artykuł jest czerwony także wtedy, gdy nie ma ani jednego piksela wypełnienia.
 */
export function kolorZapelnienia(percent: number): string {
  if (percent >= 67) return 'var(--success)';
  if (percent >= 34) return 'var(--warning)';
  return 'var(--destructive)';
}

export function PasekZapelnienia({ percent, szeroki }: { percent: number; szeroki?: boolean }) {
  /**
   * ⚠️ `Math.min`, bo stan potrafi przekroczyć pojemność planogramu: ktoś dosypał do przegródki
   * więcej, niż ustawiono w SPS. Pasek szerszy niż tor wyszedłby poza ramkę.
   */
  const wypelnienie = Math.min(100, Math.max(0, percent));
  const kolor = kolorZapelnienia(percent);

  return (
    <span
      /**
       * ⚠️ OBWÓDKA I PODBARWIONY TOR ZAMIAST SZAREGO TŁA — zgłoszone wprost: „jak jest 0 %, to jest
       * po prostu szare i nie rzuca się w oczy, a jak czegoś jest bardzo mało, to ten czerwony
       * ledwo widać”. Przy pustym artykule wypełnienia NIE MA i mieć nie może, więc jedyne miejsce,
       * w którym stan da się pokazać, to sam tor.
       *
       * ⚠️ WYSOKOŚĆ Z `h-1.5` NA `h-2`, bo ramka zjada po pikselu z góry i z dołu (`border-box`).
       * Bez tego wypełnienie zrobiłoby się cieńsze niż dotąd — czyli poprawka czytelności
       * pogorszyłaby dokładnie to, co miała naprawić przy niskich wartościach.
       */
      className={`h-2 overflow-hidden rounded-full border ${szeroki ? 'w-24' : 'w-16'}`}
      style={{
        borderColor: kolor,
        /** Ten sam idiom, co w banerach ostrzeżeń (`Agents.tsx`): kolor stanu ledwo widocznym tłem. */
        background: `color-mix(in oklab, ${kolor} 12%, transparent)`,
      }}
    >
      <span
        className="block h-full rounded-full"
        style={{
          /**
           * ⚠️ SZEROKOŚĆ ZOSTAJE DOKŁADNA, także przy 1 %. Podnoszenie jej do „minimalnej widocznej”
           * byłoby rysowaniem czegoś, czego w automacie nie ma — a to pasek, na którym ktoś opiera
           * decyzję o wyjeździe z towarem. Widoczność niskich stanów niesie obwódka, nie kłamstwo
           * o szerokości.
           */
          width: `${wypelnienie}%`,
          background: kolor,
        }}
      />
    </span>
  );
}
